import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, forkJoin, of, tap } from 'rxjs';
import { NavMenuService } from '../../../../core/components/navmenu/navmenu.service';
import { ComponentBase } from '../../../../core/_infrastructure/classes/componentBase';
import { CustomValidator } from '../../../../core/_infrastructure/classes/customValidator';
import { ForeignKeyValidator } from '../../../../core/_infrastructure/classes/foreignKeyValidator';
import { ExceptionManager } from '../../../../core/_infrastructure/classes/handlers/exceptionManager';
import { NoWhiteSpaceValidator } from '../../../../core/_infrastructure/classes/noWhiteSpaceValidator';
import { AuthService } from '../../../../core/_infrastructure/classes/services/auth.service';
import { GlobalService } from '../../../../core/_infrastructure/classes/services/global.service';
import { NotificationService } from '../../../../core/_infrastructure/classes/services/notification.service';
import { Tc } from '../../../../core/_infrastructure/types/common';
import { MunicipiosService } from '../../municipios/municipios.service';
import { PuntoInspeccionService } from '../puntoinspeccion.service';
import { PuntoInspeccionViewModel, TAduanaViewModel, TInterValoHoraPuntoInspeccionViewModel } from '../puntoinspeccion.types';

@Component({
    selector: 'app-puntoinspeccion-wrapper-edit',
    templateUrl: './puntoinspeccion-wrapper-edit.component.html',
})
export class PuntoInspeccionWrapperEditComponent extends ComponentBase implements OnInit {
    /**
     * @article: Reactive forms with smart dumb components
     * https://medium.com/angular-athens/reactive-forms-with-smart-dumb-components-48ebc81417c7
     */

    visibilitySpinner = false;
    puntoInspeccion: PuntoInspeccionViewModel;

    listPais: Tc[] | null;
    listMunicipio: Tc[] | null;
    listProvincia: Tc[] | null;
    listTipoPuntoInspeccion: Tc[] | null;
    listZona: Tc[] | null;

    controlNamesEspania: string[] = ['IdZona', 'IdProvincia', 'IdMunicipio', 'IdTipoPuntoInspeccion']; // Nombres de los controles especificos de España

    private successMessage = 'Punto Inspección guardado correctamente';

    private route = 'mantenimiento/general/puntosinspeccion';

    paisEspania = 0;

    isEspania = false;

    activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const id: any = params.get('id');
        if (id) {
            this.getById(id);
        }
    });

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        protected notificacionService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private globalService: GlobalService,
        private pifService: PuntoInspeccionService,
        private municipioService: MunicipiosService,
        private router: Router,
        private navMenuService: NavMenuService,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        this.paisEspania = parseInt(this.getConfig().IdPaisEspania);
        this.puntoInspeccion = new PuntoInspeccionViewModel();

        this.getPaises();
        this.buildForm();

        this.form.controls['IdZona'].disable();
        this.form.controls['IdProvincia'].disable();
        this.form.controls['IdMunicipio'].disable();
        this.form.controls['IdTipoPuntoInspeccion'].disable();
        this.form.controls['Localidad'].disable();
    }

    private buildForm(): void {
        this.form = this.fb.group({
            Id: 0,
            IdPais: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            IdProvincia: null,
            Codigo: '',
            Descripcion: ['', Validators.compose([Validators.required, NoWhiteSpaceValidator.cannotContainSpace])],
            IdTipoPuntoInspeccion: null,
            IdMunicipio: null,
            HabilitadoParaImportacion: false,
            HabilitadoParaExportacion: false,
            HabilitadoSanidadExterior: false,
            ReasignableEx: false,
            FinesDeSemana: false,
            NumeroInspectores: 0,
            IdZona: null,
            Email: ['', CustomValidator.emailEmptyValidator],
            Aduana: '',
            Ubicacion: '',
            HoraIntervalo: '',
            CodigoTraces: ['', Validators.compose([Validators.required, NoWhiteSpaceValidator.cannotContainSpace])],
            Activo: true,
            Localidad: ['', Validators.required],
            CodigoPostal: ['', Validators.required],
            PorcentajeSemanal: 0,
        });
    }

    /**
     * Invoca a un método global de la API para obtener la lista de países
     * @return void
     */

    private getPaises(): void {
        this.globalService.getAllPaises().subscribe((result: Tc[]) => {
            this.listPais = result;
        });
    }

    private getProvincias(idZona: number): void {
        this.globalService.getProvinciasPorIdZona(idZona).subscribe((result: Tc[]) => {
            this.listProvincia = result;
        });
    }

    private getMunicipios(idProvincia: number): void {
        this.globalService.getMunicipios(idProvincia).subscribe((result: Tc[]) => {
            this.listMunicipio = result;
        });
    }

    private getZonas(): void {
        this.globalService.getZonas().subscribe((result: Tc[]) => {
            this.listZona = result;
        });
    }

    private getTiposPuntoInspeccion(): void {
        this.globalService.getTipoPuntoInspeccion().subscribe((result: Tc[]) => {
            this.listTipoPuntoInspeccion = result;
        });
    }

    /**
     * Método que invoca a la API para cargar los datos de un pif a partir de su id.
     * @param id number
     * @return void
     */
    private getById(id: number): void {
        this.pifService.getPuntoInspeccion(id).subscribe({
            next: res => {
                this.puntoInspeccion = res;

                this.isEspania = this.puntoInspeccion.IdPais == parseInt(this.getConfig().IdPaisEspania);

                this.getProvincias(this.puntoInspeccion.IdZona ?? 0);
                this.getMunicipios(this.puntoInspeccion.IdProvincia ?? 0);
                this.getTiposPuntoInspeccion();
                this.getZonas();

                if (this.isEspania) {
                    this.addEspaniaValidators();
                }
            },
            error: () => {
                this.visibilitySpinner = false;

                this.form.disable();
            },
        });
    }

    onSelectProvincia(idProvincia: number) {
        this.globalService.getMunicipios(idProvincia).subscribe((result: Tc[]) => {
            this.listMunicipio = result;

            this.form.patchValue({
                IdMunicipio: 0,
            });

            this.form.controls['IdMunicipio'].markAsTouched();
        });
    }

    onSelectPais(idPais: number) {
        this.isEspania = idPais == parseInt(this.getConfig().IdPaisEspania);

        if (this.isEspania) {
            this.manageEspania();
        } else {
            this.managePais();
        }

        this.form.patchValue({
            IdProvincia: null,
            IdMunicipio: null,
            IdZona: null,
            IdTipoPuntoInspeccion: null,
            Codigo: '',
            CodigoTraces: '',
            Localidad: '',
        });
    }

    onSelectZona(idZona: number) {
        this.globalService.getProvinciasPorIdZona(idZona).subscribe((result: Tc[]) => {
            this.listProvincia = result;
            this.listMunicipio = null;

            this.addValidator('IdProvincia', Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])!);
            this.addValidator('IdMunicipio', Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])!);

            this.form.patchValue({
                IdProvincia: null,
                IdMunicipio: null,
            });
        });
    }

    onSelectMunicipio(idMunicipio: number) {
        if (idMunicipio > 0) {
            this.municipioService.getMunicipio(idMunicipio).subscribe(result => {
                this.form.patchValue({
                    Localidad: result.DescripcionTraces,
                });
            });
        } else {
            this.form.patchValue({
                Localidad: '',
            });
        }
    }

    onAddAduana(aduana: TAduanaViewModel) {
        this.removeValidators('Aduana');
        this.addValidator('Aduana', Validators.required);

        if (this.isFieldInvalidNoSubmit('Aduana', 'required')) {
            return;
        }

        this.removeValidators('Aduana');

        this.form.patchValue({
            Aduana: '',
        });

        this.puntoInspeccion.TAduanaCollection.push(aduana);
    }

    onDeleteAduana(aduana: TAduanaViewModel) {
        this.puntoInspeccion.TAduanaCollection.splice(this.puntoInspeccion.TAduanaCollection.indexOf(aduana), 1);
    }

    onAddIntervalo(intervalo: TInterValoHoraPuntoInspeccionViewModel) {
        this.removeValidators('HoraIntervalo');

        this.addValidator('HoraIntervalo', Validators.required);

        if (this.isFieldInvalidNoSubmit('HoraIntervalo', 'required')) {
            return;
        }
        this.removeValidators('HoraIntervalo');

        this.puntoInspeccion.TIntervaloHoraPuntoInspeccionCollection.push(intervalo);

        this.form.patchValue({
            HoraIntervalo: '',
        });
    }

    onDeleteIntervalo(intervalo: TInterValoHoraPuntoInspeccionViewModel) {
        this.puntoInspeccion.TIntervaloHoraPuntoInspeccionCollection.splice(this.puntoInspeccion.TIntervaloHoraPuntoInspeccionCollection.indexOf(intervalo), 1);
    }

    /**
     * Invocación de la API para salvar los cambios realizados en el pif
     */
    onSave(): void {
        const model = this.form.getRawValue();

        model.TAduanaCollection = this.puntoInspeccion?.TAduanaCollection;
        model.TUbicacionCollection = this.puntoInspeccion?.TUbicacionCollection;
        model.TIntervaloHoraPuntoInspeccionCollection = this.puntoInspeccion?.TIntervaloHoraPuntoInspeccionCollection;

        if (!model.HabilitadoParaExportacion) {
            model.FinesDeSemana = this.puntoInspeccion.FinesDeSemana;
            model.ReasignableEx = this.puntoInspeccion.ReasignableEx;
        } else if (!model.ReasignableEx) {
            model.FinesDeSemana = this.puntoInspeccion.FinesDeSemana;
        }

        this.submitted = true;

        if (!this.form.valid) {
            this.addErrorMessage('Revise los datos del PIF antes de guardar');

            return;
        }

        this.visibilitySpinner = true;

        this.pifService
            .save(model)
            .pipe(
                tap(() => {
                    this.visibilitySpinner = false;
                    this.addSuccessMessage(this.successMessage);
                    this.onBack();
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinner = false;

                    return of([]);
                }),
            )
            .subscribe(); // Sin subscribe, no se ejecuta la invocación al servicio
    }

    private manageEspania() {
        const listTipoPuntoInspeccion$ = this.globalService.getTipoPuntoInspeccion();
        const listZona$ = this.globalService.getZonas();

        forkJoin([listTipoPuntoInspeccion$, listZona$]).subscribe(([resTipoPuntoInspeccion, resZona]) => {
            this.listZona = resZona;
            this.listTipoPuntoInspeccion = resTipoPuntoInspeccion;

            this.addEspaniaValidators();

            this.controlNamesEspania.forEach(control => this.form.get(control)?.enable());

            this.form.controls['Localidad']?.disable();
        });
    }

    private managePais() {
        this.listProvincia = null;
        this.listTipoPuntoInspeccion = null;
        this.listZona = null;
        this.listMunicipio = null;

        this.controlNamesEspania.forEach(control => this.removeValidators(control));

        this.removeValidators('Codigo');

        this.controlNamesEspania.forEach(control => this.form.get(control)?.disable());

        //Localidad Traces
        this.form.get('Localidad')?.enable();
    }

    private addEspaniaValidators() {
        this.controlNamesEspania.forEach(control => this.addValidator(control, Validators.required));

        this.addValidator('Codigo', Validators.required);

        this.addValidator('Codigo', Validators.pattern('^[0-9]{3}$'));
    }

    get title(): string {
        return this.puntoInspeccion ? 'Editar' : 'Nuevo'; // work mode: edition or creation
    }

    /**
     * Permite volver al usuario a la página con el listado de campañas específicas.
     * @return void
     */
    onBack(): void {
        this.router.navigate([this.route]);
    }
}
